<template>
    <div class="modal is-active" v-if="show">
        <div class="modal-background" @click="cancel"></div>
        <div class="modal-card kb-import-modal">
            <header class="modal-card-head kb-import-modal__head">
                <p class="modal-card-title">{{ $t('knowledgebase.importTitle') }}</p>
                <button class="delete" @click.prevent="cancel"></button>
            </header>

            <!-- Stepper: communicate where we are and what's next. Step 1 is
                 clickable from step 2 to go back and re-analyze (e.g. if the
                 proposed structure came out in the wrong language). -->
            <div class="kb-import-modal__stepper">
                <button
                    type="button"
                    class="kb-import-modal__step"
                    :class="{'is-active': step === 'paste', 'is-clickable': step === 'review' && !submitting}"
                    :disabled="step === 'paste' || submitting"
                    @click="back"
                >
                    <span class="kb-import-modal__step-num">1</span>
                    {{ $t('knowledgebase.importStep1') }}
                </button>
                <span class="kb-import-modal__step-sep">→</span>
                <div class="kb-import-modal__step" :class="{'is-active': step === 'review'}">
                    <span class="kb-import-modal__step-num">2</span>
                    {{ $t('knowledgebase.importStep2') }}
                </div>
                <span class="kb-import-modal__step-sep">→</span>
                <div class="kb-import-modal__step">
                    <span class="kb-import-modal__step-num">3</span>
                    {{ $t('knowledgebase.importStep3') }}
                </div>
            </div>

            <!-- Step 1: paste the document -->
            <section v-if="step === 'paste'" class="modal-card-body">
                <p class="kb-import-modal__message">{{ $t('knowledgebase.importIntro') }}</p>
                <textarea
                    ref="input"
                    class="textarea kb-import-modal__textarea"
                    rows="12"
                    v-model="value"
                    :placeholder="$t('knowledgebase.importPlaceholder')"
                ></textarea>
                <p class="kb-import-modal__hint help">{{ $t('knowledgebase.importHint') }}</p>

                <label class="label kb-import-modal__label">{{ $t('knowledgebase.importInstructionsLabel') }}</label>
                <input
                    class="input"
                    type="text"
                    v-model="planInstructions"
                    :placeholder="$t('knowledgebase.importPlanInstructionsPlaceholder')"
                />
            </section>

            <!-- Step 2: review & edit the proposed structure -->
            <section v-else class="modal-card-body">
                <p class="kb-import-modal__message">{{ $t('knowledgebase.importReviewIntro') }}</p>
                <kb-structure-editor v-model="structure" />

                <label class="label kb-import-modal__label">{{ $t('knowledgebase.importInstructionsLabel') }}</label>
                <input
                    class="input"
                    type="text"
                    v-model="importInstructions"
                    :placeholder="$t('knowledgebase.importContentInstructionsPlaceholder')"
                />
            </section>

            <footer class="modal-card-foot is-clearfix">
                <template v-if="step === 'paste'">
                    <button
                        class="button is-primary"
                        :disabled="!value.trim() || planning"
                        :class="{'is-loading': planning}"
                        @click="plan"
                    >
                        {{ $t('knowledgebase.importAnalyze') }}
                    </button>
                    <button class="button" @click="cancel">{{ $t('generic.cancel') }}</button>
                </template>
                <template v-else>
                    <button
                        class="button is-primary"
                        :disabled="submitting"
                        :class="{'is-loading': submitting}"
                        @click="submit"
                    >
                        {{ $t('knowledgebase.importStart') }}
                    </button>
                    <button class="button" :disabled="submitting" @click="back">
                        {{ $t('generic.back') }}
                    </button>
                </template>
            </footer>
        </div>
    </div>
</template>

<script>
import { Api } from '../../../api';
import { EventBus } from '../../../utils';
import KbStructureEditor from './kb-structure-editor.vue';

export default {
    name: 'kb-import-modal',

    components: {KbStructureEditor},

    data() {
        return {
            show: false,
            step: 'paste', // 'paste' | 'review'
            value: '',
            planInstructions: '', // operator steering for step 1 (structure)
            importInstructions: '', // operator steering for step 2 (content)
            structure: [],
            planning: false, // step 1 request in flight
            submitting: false, // step 2 request in flight
            resolver: null,
        };
    },

    methods: {
        open() {
            this.step = 'paste';
            this.value = '';
            this.planInstructions = '';
            this.importInstructions = '';
            this.structure = [];
            this.planning = false;
            this.submitting = false;
            this.show = true;
            this.$nextTick(() => {
                if(this.$refs.input) this.$refs.input.focus();
            });
            return new Promise(resolve => {
                this.resolver = resolve;
            });
        },
        async plan() {
            const text = this.value.trim();
            if(!text || this.planning) return;
            this.planning = true;
            try {
                const res = await Api.knowledgebase.planImport(text, this.planInstructions.trim());
                const body = res.body || {};
                this.structure = body.structure || [];
                this.step = 'review';
            } catch (e) {
                EventBus.$emit(EventBus.SHOW_NOTIFICATION, {
                    title: `<h3>${this.$t('errors.500.title')}</h3>`,
                    body: this.$t('knowledgebase.importPlanError'),
                    type: 'danger',
                    direction: 'Up',
                });
            } finally {
                this.planning = false;
            }
        },
        back() {
            if(this.submitting) return;
            this.step = 'paste';
        },
        // The parent flips this on while the import request is in flight and
        // closes us once the import has been accepted.
        setSubmitting(state) {
            this.submitting = state;
        },
        close() {
            this.show = false;
        },
        submit() {
            if(this.submitting) return;
            const r = this.resolver;
            this.resolver = null;
            if(r) {
                r({
                    document: this.value.trim(),
                    structure: this.structure,
                    instructions: this.importInstructions.trim(),
                });
            }
        },
        cancel() {
            if(this.planning || this.submitting) return;
            this.show = false;
            const r = this.resolver;
            this.resolver = null;
            if(r) r(null);
        },
    },
};
</script>

<style lang="scss" scoped>
.kb-import-modal {
    max-width: 720px;
    margin: 0 auto;

    &__message {
        margin-bottom: 12px;
    }

    &__textarea {
        font-family: inherit;
    }

    &__hint {
        margin-top: 8px;
    }

    &__label {
        margin-top: 18px;
    }

    &__stepper {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 20px;
        border-bottom: 1px solid #eee;
        background: #fafafa;
        font-size: 0.85rem;
    }

    &__step {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        border: none;
        background: none;
        padding: 0;
        color: #b5b5b5;
        font-size: 0.85rem;

        &.is-active {
            color: #363636;
            font-weight: 600;
        }

        &.is-clickable {
            cursor: pointer;
            color: #3273dc;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    &__step-num {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #dbdbdb;
        color: #fff;
        font-size: 0.72rem;
        font-weight: 700;

        .is-active & {
            background: #3273dc;
        }
    }

    &__step-sep {
        color: #b5b5b5;
    }
}
</style>



// WEBPACK FOOTER //
// src/views/pages/knowledge-base/kb-import-modal.vue